/**
 * Shared ETF Metadata for Frontend
 * 
 * Single source of truth for ETF display information.
 * Import this wherever you need ticker -> name/type mapping.
 * 
 * Usage:
 *   import { getETFName, getETFMeta, ETF_META } from '@/lib/etf-metadata';
 *   
 *   getETFName('SPY') // "S&P 500"
 *   getETFMeta('GLD') // { name: 'Gold', type: 'Cmd', category: 'Commodities' }
 */

export const ETF_META = {
  // ═══════════════════════════════════════════════════════════════════════════
  // US EQUITY INDEX
  // ═══════════════════════════════════════════════════════════════════════════
  SPY:  { name: 'S&P 500', type: 'Eq', category: 'US Index' },
  QQQ:  { name: 'Nasdaq 100', type: 'Eq', category: 'US Index' },
  IWM:  { name: 'Russell 2000', type: 'Eq', category: 'US Index' },
  DIA:  { name: 'Dow 30', type: 'Eq', category: 'US Index' },
  VIXY: { name: 'VIX Futures', type: 'Vol', category: 'Volatility' },

  // ═══════════════════════════════════════════════════════════════════════════
  // STYLE FACTORS
  // ═══════════════════════════════════════════════════════════════════════════
  IWF:  { name: 'Growth', type: 'Style', category: 'Factor' },
  IWD:  { name: 'Value', type: 'Style', category: 'Factor' },
  MTUM: { name: 'Momentum', type: 'Style', category: 'Factor' },
  QUAL: { name: 'Quality', type: 'Style', category: 'Factor' },
  USMV: { name: 'Low Vol', type: 'Style', category: 'Factor' },
  SCHD: { name: 'Dividend', type: 'Style', category: 'Factor' },
  MDY:  { name: 'Mid-Cap', type: 'Style', category: 'Factor' },

  // ═══════════════════════════════════════════════════════════════════════════
  // US SECTORS
  // ═══════════════════════════════════════════════════════════════════════════
  XLF:  { name: 'Financials', type: 'Sector', category: 'Sector' },
  XLE:  { name: 'Energy', type: 'Sector', category: 'Sector' },
  XLI:  { name: 'Industrials', type: 'Sector', category: 'Sector' },
  XLV:  { name: 'Healthcare', type: 'Sector', category: 'Sector' },
  XLK:  { name: 'Technology', type: 'Sector', category: 'Sector' },
  XLY:  { name: 'Consumer Disc', type: 'Sector', category: 'Sector' },
  XLP:  { name: 'Consumer Staples', type: 'Sector', category: 'Sector' },
  XLU:  { name: 'Utilities', type: 'Sector', category: 'Sector' },
  XLB:  { name: 'Materials', type: 'Sector', category: 'Sector' },
  XLRE: { name: 'Real Estate', type: 'Sector', category: 'Sector' },
  XLC:  { name: 'Comm Services', type: 'Sector', category: 'Sector' },
  XBI:  { name: 'Biotech', type: 'Sector', category: 'Sector' },

  // ═══════════════════════════════════════════════════════════════════════════
  // INDUSTRIES
  // ═══════════════════════════════════════════════════════════════════════════
  SMH:  { name: 'Semis', type: 'Industry', category: 'Industry' },
  SOXX: { name: 'Semis (iShares)', type: 'Industry', category: 'Industry' },
  IBB:  { name: 'Biotech LC', type: 'Industry', category: 'Industry' },
  KRE:  { name: 'Regional Banks', type: 'Industry', category: 'Industry' },
  ITA:  { name: 'Aerospace/Defense', type: 'Industry', category: 'Industry' },
  ITB:  { name: 'Homebuilders', type: 'Industry', category: 'Industry' },
  IGV:  { name: 'Software', type: 'Industry', category: 'Industry' },

  // ═══════════════════════════════════════════════════════════════════════════
  // THEMATIC
  // ═══════════════════════════════════════════════════════════════════════════
  KWEB: { name: 'China Internet', type: 'Theme', category: 'Thematic' },
  ICLN: { name: 'Clean Energy', type: 'Theme', category: 'Thematic' },
  TAN:  { name: 'Solar', type: 'Theme', category: 'Thematic' },
  BOTZ: { name: 'Robotics/AI', type: 'Theme', category: 'Thematic' },
  HACK: { name: 'Cybersecurity', type: 'Theme', category: 'Thematic' },
  ARKK: { name: 'ARK Innovation', type: 'Theme', category: 'Thematic' },

  // ═══════════════════════════════════════════════════════════════════════════
  // DEVELOPED MARKETS
  // ═══════════════════════════════════════════════════════════════════════════
  EFA:  { name: 'EAFE', type: 'Intl', category: 'Developed' },
  VGK:  { name: 'Europe', type: 'Intl', category: 'Developed' },
  EWU:  { name: 'UK', type: 'Intl', category: 'Developed' },
  EWG:  { name: 'Germany', type: 'Intl', category: 'Developed' },
  EWQ:  { name: 'France', type: 'Intl', category: 'Developed' },
  FEZ:  { name: 'Euro STOXX 50', type: 'Intl', category: 'Developed' },
  EWJ:  { name: 'Japan', type: 'Intl', category: 'Developed' },
  EPP:  { name: 'Pacific ex-JP', type: 'Intl', category: 'Developed' },
  DXJ:  { name: 'Japan Hedged', type: 'Intl', category: 'Developed' },

  // ═══════════════════════════════════════════════════════════════════════════
  // EMERGING MARKETS
  // ═══════════════════════════════════════════════════════════════════════════
  EEM:  { name: 'EM Broad', type: 'EM', category: 'Emerging' },
  AAXJ: { name: 'Asia ex-JP', type: 'EM', category: 'Emerging' },
  FXI:  { name: 'China LC', type: 'EM', category: 'Emerging' },
  MCHI: { name: 'China Broad', type: 'EM', category: 'Emerging' },
  ASHR: { name: 'China A-Shares', type: 'EM', category: 'Emerging' },
  INDA: { name: 'India', type: 'EM', category: 'Emerging' },
  EWT:  { name: 'Taiwan', type: 'EM', category: 'Emerging' },
  EWZ:  { name: 'Brazil', type: 'EM', category: 'Emerging' },
  ILF:  { name: 'Latin America', type: 'EM', category: 'Emerging' },
  EWW:  { name: 'Mexico', type: 'EM', category: 'Emerging' },

  // ═══════════════════════════════════════════════════════════════════════════
  // FIXED INCOME - RATES
  // ═══════════════════════════════════════════════════════════════════════════
  TLT:  { name: '20Y+ Treasury', type: 'FI', category: 'Rates' },
  IEF:  { name: '7-10Y Treasury', type: 'FI', category: 'Rates' },
  SHY:  { name: '1-3Y Treasury', type: 'FI', category: 'Rates' },
  GOVT: { name: 'Treasury Broad', type: 'FI', category: 'Rates' },
  TIP:  { name: 'TIPS', type: 'FI', category: 'Rates' },
  BND:  { name: 'Total Bond', type: 'FI', category: 'Rates' },

  // ═══════════════════════════════════════════════════════════════════════════
  // FIXED INCOME - CREDIT
  // ═══════════════════════════════════════════════════════════════════════════
  LQD:  { name: 'IG Corporate', type: 'FI', category: 'Credit' },
  HYG:  { name: 'High Yield', type: 'FI', category: 'Credit' },
  EMB:  { name: 'EM Sovereign $', type: 'FI', category: 'Credit' },
  EMLC: { name: 'EM Local Ccy', type: 'FI', category: 'Credit' },

  // ═══════════════════════════════════════════════════════════════════════════
  // COMMODITIES
  // ═══════════════════════════════════════════════════════════════════════════
  USO:  { name: 'WTI Crude', type: 'Cmd', category: 'Energy' },
  BNO:  { name: 'Brent Crude', type: 'Cmd', category: 'Energy' },
  UNG:  { name: 'Natural Gas', type: 'Cmd', category: 'Energy' },
  URA:  { name: 'Uranium', type: 'Cmd', category: 'Energy' },
  GLD:  { name: 'Gold', type: 'Cmd', category: 'Metals' },
  SLV:  { name: 'Silver', type: 'Cmd', category: 'Metals' },
  PPLT: { name: 'Platinum', type: 'Cmd', category: 'Metals' },
  CPER: { name: 'Copper', type: 'Cmd', category: 'Metals' },
  DBB:  { name: 'Base Metals', type: 'Cmd', category: 'Metals' },
  DBC:  { name: 'Commodity Idx', type: 'Cmd', category: 'Broad' },
  DBA:  { name: 'Agriculture', type: 'Cmd', category: 'Agri' },
  WEAT: { name: 'Wheat', type: 'Cmd', category: 'Agri' },
  CORN: { name: 'Corn', type: 'Cmd', category: 'Agri' },

  // ═══════════════════════════════════════════════════════════════════════════
  // CURRENCIES
  // ═══════════════════════════════════════════════════════════════════════════
  UUP:  { name: 'US Dollar', type: 'FX', category: 'Currency' },
  FXE:  { name: 'Euro', type: 'FX', category: 'Currency' },
  FXY:  { name: 'Yen', type: 'FX', category: 'Currency' },
  FXB:  { name: 'Pound', type: 'FX', category: 'Currency' },

  // ═══════════════════════════════════════════════════════════════════════════
  // CRYPTO
  // ═══════════════════════════════════════════════════════════════════════════
  BITO: { name: 'Bitcoin Futures', type: 'Crypto', category: 'Crypto' },
  IBIT: { name: 'Bitcoin Spot', type: 'Crypto', category: 'Crypto' },
  FBTC: { name: 'Bitcoin (Fidelity)', type: 'Crypto', category: 'Crypto' },

  // ═══════════════════════════════════════════════════════════════════════════
  // REAL ESTATE
  // ═══════════════════════════════════════════════════════════════════════════
  VNQ:  { name: 'REITs', type: 'RE', category: 'Real Estate' },
  IYR:  { name: 'US Real Estate', type: 'RE', category: 'Real Estate' },
};

/**
 * Get ETF display name (fallback to ticker if not found)
 */
export function getETFName(ticker) {
  return ETF_META[ticker?.toUpperCase()]?.name || ticker;
}

/**
 * Get full ETF metadata
 */
export function getETFMeta(ticker) {
  return ETF_META[ticker?.toUpperCase()] || { name: ticker, type: '?', category: 'Unknown' };
}

/**
 * Format ticker with name for display
 * @example formatTickerDisplay('SPY') => "SPY • S&P 500"
 */
export function formatTickerDisplay(ticker, separator = ' • ') {
  const name = getETFName(ticker);
  return name !== ticker ? `${ticker}${separator}${name}` : ticker;
}

/**
 * Get type badge color for UI
 */
export function getTypeColor(type) {
  const colors = {
    Eq: 'text-blue-400',
    Sector: 'text-blue-400',
    Industry: 'text-cyan-400',
    Style: 'text-purple-400',
    Theme: 'text-pink-400',
    Intl: 'text-sky-400',
    EM: 'text-orange-400',
    FI: 'text-green-400',
    Cmd: 'text-amber-400',
    FX: 'text-teal-400',
    Crypto: 'text-yellow-400',
    Vol: 'text-red-400',
    RE: 'text-lime-400',
  };
  return colors[type] || 'text-gray-400';
}

export default ETF_META;
